import { createContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User as SupabaseUser } from '@supabase/supabase-js';
import { User, Role, ProfileStatus, Profile } from '../types/auth';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { getOrCreateProfile, updateProfile as updateProfileService, ensureAdmin } from '../services/profileService';
import { linkOrphanLeadsToUser } from '../services/leadsLinkService';
import { SUPABASE_TIMEOUT, SUPABASE_RETRY_COUNT, SUPABASE_RETRY_DELAY } from '../lib/envConfig';
import { sendTransactionalEmail, prepareNewCandidatureAdminEmail } from '../services/resendService';

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  isAdmin: boolean;
  error: string | null;
  signUp: (email: string, password: string, metadata: { full_name?: string; role?: Role }) => Promise<{ error?: string }>;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  updateProfile: (updates: { full_name?: string; phone?: string; city?: string; country?: string }) => Promise<{ error?: string }>;
  refreshProfile: () => Promise<void>;
  setRole: (role: Role) => void;
  setStatus: (status: ProfileStatus) => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

async function withTimeout<T>(promise: PromiseLike<T> | T, timeoutMs: number = SUPABASE_TIMEOUT): Promise<Awaited<T>> {
  return Promise.race([
    Promise.resolve(promise) as Promise<Awaited<T>>,
    new Promise<Awaited<T>>((_, reject) =>
      setTimeout(() => reject(new Error('Timeout: requête trop longue')), timeoutMs)
    ),
  ]);
}

async function withRetry<T>(
  fn: () => PromiseLike<T> | T,
  retries: number = SUPABASE_RETRY_COUNT,
  delay: number = SUPABASE_RETRY_DELAY
): Promise<Awaited<T>> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await withTimeout(fn());
    } catch (err) {
      lastError = err;
      const isTimeout = err instanceof Error && err.message.includes('Timeout');
      const isNetworkError = err instanceof Error && (
        err.message.includes('fetch') ||
        err.message.includes('network') ||
        err.message.includes('Failed to fetch')
      );
      if ((isTimeout || isNetworkError) && attempt < retries) {
        if (import.meta.env.DEV) console.warn(`⚠️ [AUTH] Tentative ${attempt + 1}/${retries} échouée, retry dans ${delay}ms...`);
        await new Promise(res => setTimeout(res, delay * (attempt + 1)));
        continue;
      }
      break;
    }
  }
  throw lastError;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastFetchedUserId = useRef<string | null>(null);

  const showDebug = typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).get('qa') === '1' || import.meta.env.DEV);

  const fetchUserProfile = async (supabaseUser: SupabaseUser, force = false) => {
    if (!force && lastFetchedUserId.current === supabaseUser.id && user !== null) {
      if (showDebug) console.log('⚡ [AUTH] Profil déjà chargé pour:', supabaseUser.email, '— skip');
      return;
    }

    try {
      if (showDebug) console.log('🔍 [AUTH] fetchUserProfile pour:', supabaseUser.email);

      if (!isSupabaseConfigured()) throw new Error('Supabase non configuré');

      const isKarlAdmin = supabaseUser.email === 'karlcibois@gmail.com';

      if (isKarlAdmin) {
        ensureAdmin(supabaseUser).catch(e => {
          if (import.meta.env.DEV) console.warn('⚠️ [AUTH] ensureAdmin bg:', e);
        });

        // Charger le vrai profil depuis la DB
        const { data: karlProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', supabaseUser.id)
          .maybeSingle();

        const profileData: Profile = karlProfile
          ? (karlProfile as Profile)
          : {
              user_id: supabaseUser.id,
              email: supabaseUser.email ?? null,
              full_name: null,
              phone: null,
              city: null,
              country: null,
              role: 'ADMIN',
              status: 'VALIDATED',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };

        setProfile({ ...profileData, role: 'ADMIN', status: 'VALIDATED' });
        setIsAdmin(true);
        setUser({
          user_id: supabaseUser.id,
          email: supabaseUser.email || '',
          full_name: profileData.full_name ?? undefined,
          role: 'ADMIN',
          status: 'VALIDATED',
        });
        lastFetchedUserId.current = supabaseUser.id;
        setError(null);
        return;
      }

      const fetchAdminLevel = async (): Promise<{
        data: { level: string } | null;
        error: { code?: string } | null;
      }> => {
        const response = await supabase
          .from('admin_users')
          .select('level')
          .eq('user_id', supabaseUser.id)
          .maybeSingle();
        return {
          data: response.data as { level: string } | null,
          error: response.error ? { code: response.error.code } : null,
        };
      };

      const [profileResult, adminResult] = await Promise.allSettled([
        withRetry(() => getOrCreateProfile(supabaseUser)),
        withRetry(fetchAdminLevel),
      ]);

      let profileData: Profile;
      if (profileResult.status === 'fulfilled') {
        profileData = profileResult.value;
        if (showDebug) console.log('✅ [AUTH] Profil:', { role: profileData.role, status: profileData.status });
      } else {
        if (import.meta.env.DEV) console.error('❌ [AUTH] getOrCreateProfile échoué:', profileResult.reason);
        // Profil de secours pour ne pas bloquer l'utilisateur
        profileData = {
          user_id: supabaseUser.id,
          email: supabaseUser.email ?? null,
          full_name: null,
          phone: null,
          city: null,
          country: null,
          role: 'COMMERCE',
          status: 'NEW',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      }

      let adminData = null;
      if (adminResult.status === 'fulfilled') {
        const { data, error: adminError } = adminResult.value;
        if (!adminError || adminError.code === 'PGRST116') adminData = data;
      }

      const computedIsAdmin = !!adminData || profileData.role === 'ADMIN';

      setProfile(profileData);
      setIsAdmin(computedIsAdmin);
      setUser({
        user_id: supabaseUser.id,
        email: supabaseUser.email || '',
        full_name: profileData.full_name ?? undefined,
        role: profileData.role ?? undefined,
        status: profileData.status ?? undefined,
      });
      lastFetchedUserId.current = supabaseUser.id;
      setError(null);

      linkOrphanLeadsToUser(supabaseUser.id, supabaseUser.email ?? '').then(({ inferredRole, inferredStatus }) => {
        if (
          (inferredRole && inferredRole !== profileData.role) ||
          (inferredStatus && inferredStatus !== profileData.status)
        ) {
          if (showDebug) console.log('🔄 [AUTH] Mise à jour role/status depuis leads:', { inferredRole, inferredStatus });
          setUser(prev => prev ? {
            ...prev,
            role: (inferredRole as Role) || prev.role,
            status: (inferredStatus as ProfileStatus) || prev.status,
          } : null);
        }
      }).catch(e => {
        if (import.meta.env.DEV) console.warn('⚠️ [AUTH] linkOrphanLeads bg:', e);
      });

    } catch (err) {
      if (import.meta.env.DEV) console.error('❌ [AUTH] fetchUserProfile exception:', err);
      const isKarlAdmin = supabaseUser.email === 'karlcibois@gmail.com';
      // En cas d'erreur, on connecte quand même l'utilisateur avec des données minimales
      setUser({
        user_id: supabaseUser.id,
        email: supabaseUser.email || '',
        role: isKarlAdmin ? 'ADMIN' : undefined,
      });
      setProfile(null);
      setIsAdmin(isKarlAdmin);
      // Ne pas afficher l'erreur timeout à l'utilisateur, juste logger
      const isTimeout = err instanceof Error && err.message.includes('Timeout');
      if (!isTimeout) {
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
      } else {
        setError(null);
      }
    }
  };

  const refreshProfile = async () => {
    try {
      const { data: { session } } = await withTimeout(supabase.auth.getSession());
      if (session?.user) {
        await fetchUserProfile(session.user, true);
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('Error refreshing profile:', err);
      // Ne pas propager l'erreur réseau à l'utilisateur
      const isNetworkError = err instanceof Error && (
        err.message.includes('fetch') ||
        err.message.includes('Failed to fetch') ||
        err.message.includes('network')
      );
      if (!isNetworkError) {
        setError(err instanceof Error ? err.message : 'Erreur lors du rafraîchissement');
      }
    }
  };

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setError('Supabase non configuré');
      setLoading(false);
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        fetchUserProfile(session.user).finally(() => setLoading(false));
      } else {
        setUser(null);
        setProfile(null);
        setIsAdmin(false);
        setLoading(false);
      }
    }).catch((err) => {
      console.error('Error getting session:', err);
      setError(err instanceof Error ? err.message : 'Erreur de session');
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (showDebug) console.log('🔔 [AUTH] onAuthStateChange:', event);

        if (session?.user) {
          const force = event === 'SIGNED_IN';
          await fetchUserProfile(session.user, force);
        } else {
          setUser(null);
          setProfile(null);
          setIsAdmin(false);
          lastFetchedUserId.current = null;
        }
        setLoading(false);
      }
    );

    return () => { subscription.unsubscribe(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async (
    email: string,
    password: string,
    metadata: { full_name?: string; role?: Role }
  ): Promise<{ error?: string }> => {
    try {
      if (!isSupabaseConfigured()) return { error: 'Service d\'authentification non configuré' };

      // Validation email
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        return { error: 'Adresse email invalide' };
      }

      // Validation password
      if (!password || password.length < 6) {
        return { error: 'Le mot de passe doit contenir au moins 6 caractères' };
      }

      const { data, error } = await withTimeout(
        supabase.auth.signUp({ email, password, options: { data: metadata } })
      );

      if (error) {
        const errorLower = error.message.toLowerCase();
        if (errorLower.includes('rate limit') || errorLower.includes('too many')) return { error: 'RATE_LIMIT' };
        if (errorLower.includes('already') && (errorLower.includes('registered') || errorLower.includes('exists'))) return { error: 'USER_EXISTS' };
        return { error: error.message };
      }

      if (!data.user) return { error: 'Erreur lors de la création du compte' };

      try {
        const existingProfile = await withTimeout(getOrCreateProfile(data.user));
        if (!existingProfile.role || (metadata.role && existingProfile.role !== metadata.role)) {
          await withTimeout(
            supabase.from('profiles').update({
              role: metadata.role,
              full_name: metadata.full_name || existingProfile.full_name,
              updated_at: new Date().toISOString(),
            }).eq('user_id', data.user.id)
          );
        }

        // ✅ Créer automatiquement un lead lié à l'utilisateur
        try {
          await withTimeout(
            supabase.from('leads').insert({
              user_id: data.user.id,
              type: metadata.role || 'AO',
              source: 'signup',
              status: 'NEW',
              pipeline_step: 'ACCOUNT_CREATED',
            })
          );
          if (import.meta.env.DEV) {
            console.log('[AuthContext] ✅ Lead créé pour:', email, 'type:', metadata.role);
          }
        } catch (leadErr) {
          if (import.meta.env.DEV) {
            console.warn('[AuthContext] ⚠️ Erreur création lead:', leadErr);
          }
        }

        // ✅ Envoyer un email de notification à l'admin pour la nouvelle inscription
        try {
          const adminEmail = prepareNewCandidatureAdminEmail({
            candidateName: metadata.full_name || email,
            candidateEmail: email,
            candidatePhone: null,
            candidateCity: null,
            role: (metadata.role as 'AO' | 'COMMERCE') || 'AO',
          });
          await sendTransactionalEmail(adminEmail);
          if (import.meta.env.DEV) {
            console.log('[AuthContext] ✅ Email admin envoyé pour nouvelle inscription:', email);
          }
        } catch (emailErr) {
          if (import.meta.env.DEV) {
            console.warn('[AuthContext] ⚠️ Erreur envoi email admin inscription:', emailErr);
          }
        }

        return {};
      } catch (profileErr) {
        console.error('❌ [AUTH] Erreur profil signup:', profileErr);
        const { data: existing } = await supabase.from('profiles').select('*').eq('user_id', data.user.id).maybeSingle();
        if (existing) {
          if (!existing.role || (metadata.role && existing.role !== metadata.role)) {
            await supabase.from('profiles').update({
              role: metadata.role,
              full_name: metadata.full_name || existing.full_name,
              updated_at: new Date().toISOString(),
            }).eq('user_id', data.user.id);
          }

          // ✅ Créer le lead même en cas de fallback profil
          try {
            await supabase.from('leads').insert({
              user_id: data.user.id,
              type: metadata.role || 'AO',
              source: 'signup',
              status: 'NEW',
              pipeline_step: 'ACCOUNT_CREATED',
            });
          } catch {
            // Silencieux
          }

          // ✅ Envoyer l'email admin même en cas de fallback profil
          try {
            const adminEmail = prepareNewCandidatureAdminEmail({
              candidateName: metadata.full_name || email,
              candidateEmail: email,
              candidatePhone: null,
              candidateCity: null,
              role: (metadata.role as 'AO' | 'COMMERCE') || 'AO',
            });
            await sendTransactionalEmail(adminEmail);
          } catch {
            // Silencieux — ne pas bloquer l'inscription
          }

          return {};
        }
        return { error: 'PROFILE_CREATE_FAILED' };
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Une erreur est survenue lors de l\'inscription';
      return { error: message };
    }
  };

  const signIn = async (email: string, password: string): Promise<{ error?: string }> => {
    try {
      if (!isSupabaseConfigured()) return { error: 'Service d\'authentification non configuré' };

      // Validation email
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        return { error: 'Adresse email invalide' };
      }

      // Validation password
      if (!password || password.length < 1) {
        return { error: 'Mot de passe requis' };
      }

      const { error } = await withTimeout(supabase.auth.signInWithPassword({ email, password }));
      if (error) return { error: error.message };
      return {};
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Une erreur est survenue lors de la connexion' };
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      if (!isSupabaseConfigured()) {
        setUser(null); setProfile(null); setIsAdmin(false);
        lastFetchedUserId.current = null;
        return;
      }
      await withTimeout(supabase.auth.signOut());
      lastFetchedUserId.current = null;
    } catch (err) {
      console.error('SignOut exception:', err);
    }
  };

  const updateProfile = async (updates: { full_name?: string; phone?: string; city?: string; country?: string }): Promise<{ error?: string }> => {
    if (!user) return { error: 'Utilisateur non connecté' };
    if (!isSupabaseConfigured()) return { error: 'Service non configuré' };
    try {
      await withTimeout(updateProfileService(user.user_id, updates));
      await refreshProfile();
      return {};
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Une erreur est survenue lors de la mise à jour' };
    }
  };

  const setRole = (role: Role) => {
    if (user) setUser(prev => prev ? { ...prev, role } : null);
  };

  const setStatus = (status: ProfileStatus) => {
    if (user) setUser(prev => prev ? { ...prev, status } : null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, isAdmin, error, signUp, signIn, signOut, updateProfile, refreshProfile, setRole, setStatus }}>
      {children}
    </AuthContext.Provider>
  );
}
